/**
 * Página de Checkout Directo
 * Ruta: /buy/:slug
 */

import { useEffect, useState } from "react";
import { getCanonicalLocation } from "@/lib/canonicalLocation";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CreditCard, Lock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { flushPostHogBeforeNavigation } from "@/lib/posthogAnalytics";

interface Product {
  id: string;
  name: string;
  description: string;
  slug: string;
  price_usd: number;
  price_ars?: number;
  price_brl?: number;
  price_clp?: number;
  price_mxn?: number;
  recommended_price: number;
  recommended_currency: string;
  product_type: string;
  product_category: string;
  plan_tier?: string;
  features: string[];
  stripe_price_id?: string;
  mercadopago_plan_id?: string;
}

export default function DirectCheckout() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    phone: "",
  });

  useEffect(() => {
    if (!slug) return;

    const detectCountry = async () => {
      // VERDAD ÚNICA: país del Worker (canonicalLocation); fallback al
      // endpoint server-side (headers CF). Nunca proveedores geo del cliente.
      const canonical = getCanonicalLocation().country;
      if (canonical) return canonical;
      try {
        const res = await fetch("/api/detect-country");
        const data = await res.json();
        return data.country && data.country !== "XX" ? data.country : "US";
      } catch {
        return "US";
      }
    };

    detectCountry().then((country) => {
      fetch(`/api/products/by-slug?slug=${slug}&country=${country}`)
        .then(async (res) => {
          if (!res.ok) {
            throw new Error(t("directCheckout.productNotFound"));
          }
          return res.json();
        })
        .then((data) => {
          setProduct(data.product);
          setLoading(false);
        })
        .catch((err: Error) => {
          toast({
            title: t("directCheckout.errorTitle"),
            description: err.message,
            variant: "destructive",
          });
          setLoading(false);
        });
    });
  }, [slug, toast, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProcessing(true);

    try {
      if (!formData.email || !formData.name) {
        throw new Error(t("directCheckout.fillFields"));
      }

      const origin = window.location.origin;

      if (product?.stripe_price_id) {
        const amountCents = Math.round(Number(product.price_usd || product.recommended_price) * 100);

        // Coherence with full funnel integration: flush before Stripe redirect.
        await flushPostHogBeforeNavigation(350);

        const response = await fetch("/api/create-subscription-checkout?redirect=1", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Checkout-Redirect": "1",
          },
          body: JSON.stringify({
            amount: amountCents,
            productName: product.name,
            buyerEmail: formData.email,
            successUrl: `${origin}/muchas-gracias`,
            cancelUrl: `${origin}/buy/${slug}`,
            metadata: {
              plan_tier: product.plan_tier || "standard",
              flow: "direct_checkout",
              product_id: product.id,
              customer_name: formData.name,
              source: "delfuocampo", // will be normalized server-side to BRAND_SOURCE_KEY
            },
          }),
          redirect: "manual",
        });

        const redirectUrl =
          response.headers.get("Location") ||
          (response.ok ? (await response.json().catch(() => ({}))).url : null);
        if (!redirectUrl) throw new Error(t("directCheckout.processError"));
        window.location.href = redirectUrl;
        return;
      }

      if (product?.mercadopago_plan_id) {
        const response = await fetch("/api/mercadopago/create-preference", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            productName: product.name,
            price: product.recommended_price,
            currency: product.recommended_currency || "ARS",
            country: "AR",
            payerEmail: formData.email,
            successPath: `/muchas-gracias`,
            failurePath: `/buy/${slug}`,
            pendingPath: `/buy/${slug}`,
            metadata: {
              flow: "direct_checkout",
              product_id: product.id,
              customer_name: formData.name,
            },
          }),
        });

        if (!response.ok) throw new Error(t("directCheckout.processError"));
        const { init_point } = await response.json();
        window.location.href = init_point;
        return;
      }

      toast({
        title: t("directCheckout.processingTitle"),
        description: t("directCheckout.redirectingCheckout"),
      });

      localStorage.setItem(
        "checkoutData",
        JSON.stringify({
          product,
          customer: formData,
        })
      );

      navigate(`/checkout?product=${product?.id}`);
    } catch (error) {
      toast({
        title: t("directCheckout.errorTitle"),
        description: error instanceof Error ? error.message : t("directCheckout.processError"),
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>{t("directCheckout.notFoundTitle")}</CardTitle>
            <CardDescription>{t("directCheckout.notFoundDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button type="button" onClick={() => navigate("/")}>
              {t("directCheckout.home")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">{t("directCheckout.orderSummary")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold text-lg">{product.name}</h3>
                  <p className="text-sm text-gray-600 mt-2">{product.description}</p>
                </div>

                {product.features && product.features.length > 0 && (
                  <div className="pt-4 border-t">
                    <h4 className="font-semibold mb-2">{t("directCheckout.includes")}</h4>
                    <ul className="space-y-2">
                      {product.features.map((feature, idx) => (
                        <li key={idx} className="text-sm flex items-start gap-2">
                          <span className="text-green-500">✓</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="pt-4 border-t">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t("directCheckout.subtotal")}</span>
                    <span className="font-semibold">
                      {product.recommended_currency} {product.recommended_price}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2 text-lg font-bold">
                    <span>{t("directCheckout.total")}</span>
                    <span className="text-purple-600">
                      {product.recommended_currency} {product.recommended_price}
                      {product.product_type === "recurring" && (
                        <span className="text-sm text-gray-500"> {t("directCheckout.perMonth")}</span>
                      )}
                    </span>
                  </div>
                </div>

                <div className="pt-4 border-t text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    <span>{t("directCheckout.securePay")}</span>
                  </div>
                  <p className="mt-2">{t("directCheckout.secureNote")}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  {t("directCheckout.paymentInfo")}
                </CardTitle>
                <CardDescription>{t("directCheckout.completeData")}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="email">{t("directCheckout.email")}</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="you@email.com"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">{t("directCheckout.emailHint")}</p>
                  </div>

                  <div>
                    <Label htmlFor="name">{t("directCheckout.fullName")}</Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Jane Doe"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">{t("directCheckout.phoneOptional")}</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1 555 000 0000"
                    />
                  </div>

                  <Button type="submit" className="w-full" size="lg" disabled={processing}>
                    {processing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t("directCheckout.processing")}
                      </>
                    ) : (
                      <>
                        <Lock className="mr-2 h-4 w-4" />
                        {t("directCheckout.payCta", {
                          currency: product.recommended_currency,
                          price: String(product.recommended_price),
                        })}
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-gray-500">{t("directCheckout.termsHint")}</p>
                </form>
              </CardContent>
            </Card>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => navigate(`/gift/${slug}`)}
                className="text-sm text-gray-600 hover:text-gray-900 underline"
              >
                {t("directCheckout.moreDetails")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
